import type {
    AssetOption,
    OptionBuyingStrategy,
    TradeTransactionWithFinancials,
    Entity,
    UserPortfolioPosition,
    UserPortfolioTrade
} from '@einhaus/project314-types';

import { getDateFromUnix, getDatePlusMinus, trimDecimals } from '@einhaus/meteoric-helpers';
import { calculateUserPortfolioTradePerformance, closeTradeTypes } from '@einhaus/project314-shared';
import EntityLink from '@/components/Entity/EntityLink.tsx';
import OptionLink from '@/components/Entity/OptionLink.tsx';

export const getTradeOutput = (details: {
    trade: UserPortfolioTrade;
    entity: Entity;
    option?: AssetOption | null;
    optionBuyingStrategies?: OptionBuyingStrategy[];
}) => {
    const { trade, entity, option, optionBuyingStrategies } = details;

    const transactions = trade.transactions.slice().sort((first, second) => first.timestamp - second.timestamp);
    const openingTxns = transactions.filter((transaction) => transaction.isOpeningTrade);
    const performance = calculateUserPortfolioTradePerformance(trade);
    if (!performance) return;

    const { profit, profitPercentage, startingBalance, endingBalance } = performance;

    if (!openingTxns[0]) return;
    const closingTrades = transactions.filter((transaction) => closeTradeTypes.includes(transaction.type));
    const lastClosingTrade = closingTrades.at(-1);
    if (!lastClosingTrade) return;

    const { fees } = performance;

    // Include entity data for React components
    const entityData = entity
        ? {
              entityId: entity.id,
              entityType: entity.entityType,
              entitySymbol: entity.symbol,
              entityName: entity.name
          }
        : null;

    const percentageChange = profitPercentage;

    const changeColorClass = percentageChange > 0 ? 'text-profit-green' : percentageChange < 0 ? 'text-red-500' : 'text-zinc-400';

    const changeColorClassProfit = profit > 0 ? 'text-profit-green' : profit < 0 ? 'text-red-500' : 'text-zinc-400';

    const optionBuyingStrategy = openingTxns[0]?.optionBuyingStrategyId
        ? optionBuyingStrategies?.find((s) => s.id === openingTxns[0].optionBuyingStrategyId)
        : null;

    // Store option data for React components
    let optionData = null;
    let optionLink = null;

    const entityLink = <EntityLink showName showSymbol entity={entity} />;

    if (option) {
        const startDateTime = getDatePlusMinus('-', 1, 'day', 'ymdhms', 'start', openingTxns[0]?.timestamp);

        const endDateTime = getDatePlusMinus('+', 1, 'day', 'ymdhms', 'end', lastClosingTrade.timestamp);

        optionLink = (
            <OptionLink
                description={option.description}
                entity={{ entityId: entity.id, entityType: entity.entityType }}
                optionId={option.id}
            />
        );

        // Store option data for React components
        optionData = {
            optionId: option.id,
            description: option.description,
            startDateTime,
            endDateTime
        };
    }

    return {
        entityLink,
        optionLink,
        direction: trade.direction,
        optionBuyingStrategy: optionBuyingStrategy?.name ?? '',
        startingValue: `$${trimDecimals(startingBalance)}`,
        endingValue: `$${trimDecimals(endingBalance)}`,
        fees: `$${trimDecimals(fees)}`,
        changeOutput: <span className={changeColorClass}>{percentageChange}%</span>,
        profitOutput: <span className={changeColorClassProfit}>${profit}</span>,
        entryDate: getDateFromUnix(openingTxns[0].timestamp, 'readable1', 'America/New_York'),
        exitDate: getDateFromUnix(lastClosingTrade.timestamp, 'readable1', 'America/New_York'),
        entityData,
        optionData
    };
};

export const getTransactionsRiskCapital = (transactions: TradeTransactionWithFinancials[]) => {
    let riskCapital = 0;

    // TODO - We need to proportionally remove risk capital for closed trades?
    for (const transaction of transactions) {
        if (!transaction.riskCapital) continue;
        riskCapital += transaction.riskCapital;
    }

    return riskCapital;
};

export const getOpenPositionRiskCapital = (config: {
    position: Pick<
        UserPortfolioPosition,
        | 'derivativeType'
        | 'openTransactionType'
        | 'startingValue'
        | 'entryPrice'
        | 'quantity'
        | 'contractMultiplier'
        | 'legId'
        | 'legNumber'
    >;
    transactions: TradeTransactionWithFinancials[];
}) => {
    const { position, transactions } = config;

    const positionTransactions = transactions.filter(
        (transaction) => transaction.legId === position.legId && transaction.legNumber === position.legNumber
    );

    const matchingPositionBasisTransaction = positionTransactions.find((transaction) => {
        if (position.openTransactionType === 'assignmentBuy') return transaction.type === 'assignmentBuy';

        return transaction.isOpeningTrade && transaction.type === position.openTransactionType;
    });

    if (position.derivativeType === 'Option' && position.openTransactionType !== 'sellToOpen') {
        return matchingPositionBasisTransaction?.value ?? 0;
    }

    if (matchingPositionBasisTransaction?.riskCapital) {
        return matchingPositionBasisTransaction.riskCapital;
    }

    return position.startingValue || trimDecimals(position.quantity * position.entryPrice * position.contractMultiplier);
};

export const getOpenPositionPriceImpactBasis = (
    position: Pick<UserPortfolioPosition, 'entryPrice' | 'lastClosePrice' | 'quantity' | 'contractMultiplier' | 'startingValue'>
) => {
    const { entryPrice, lastClosePrice, quantity, startingValue, contractMultiplier } = position;

    if (!lastClosePrice) return entryPrice;

    if (quantity * contractMultiplier > 1 && Math.abs(lastClosePrice - startingValue) <= 0.01) {
        return trimDecimals(startingValue / (quantity * contractMultiplier), 4);
    }

    return lastClosePrice;
};
